/**
 * BlogCover — the cover treatment shared by post cards, the featured hero, and
 * the article hero.
 *
 * The cover screenshots are wide, dense captures of the tools, so shown at card
 * size their content is illegible. The image fills the slot edge-to-edge and is
 * zoomed in to surface a readable region, with a blue→purple brand wash layered
 * over it for depth and a touch of color. (Dedicated, tighter screenshots for
 * desktop and mobile are the real fix; the zoom is an interim legibility aid.)
 */

// Cache-busting token for blog cover images. Cover files are overwritten in
// place (stable paths), so bump this whenever covers are re-captured to defeat
// Cloudflare's edge cache.
const COVER_VERSION = "20260621b";

type CoverSize = "card" | "featured" | "hero";

interface BlogCoverProps {
  /** Desktop screenshot (16:9). */
  coverImage: string | null;
  /** Phone-proportioned screenshot, shown at <=640px when present. */
  coverImageMobile?: string | null;
  alt: string;
  size?: CoverSize;
  /** Wrapper classes — usually the aspect ratio / min-height for the slot. */
  className?: string;
  priority?: boolean;
}

export function BlogCover({
  coverImage,
  coverImageMobile,
  alt,
  className,
  priority = false,
}: BlogCoverProps) {
  // Cover files keep stable paths (e.g. .../verstappen-...-desktop.png) but their
  // CONTENT is updated in place. Cloudflare caches them for hours, so an overwrite
  // would keep serving the old image. Append a version so a content change shows
  // up immediately (CF treats it as a new URL). Bump COVER_VERSION when covers
  // are re-captured.
  const v = `?v=${COVER_VERSION}`;
  return (
    <div className={`relative isolate overflow-hidden ${className ?? ""}`}>
      {coverImage ? (
        <picture>
          {coverImageMobile && (
            <source
              media="(max-width: 640px)"
              srcSet={`${coverImageMobile}${v}`}
            />
          )}
          <img
            src={`${coverImage}${v}`}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
          />
        </picture>
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-primary/30 via-accent/20 to-secondary/30" />
      )}

      {/* Blue → purple brand wash over the capture — subtle tint */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(130%_120%_at_0%_0%,oklch(0.5_0.22_260/0.22),transparent_58%),radial-gradient(130%_120%_at_100%_100%,oklch(0.62_0.25_330/0.18),transparent_58%)]"
      />
      {/* Subtle top sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-px bg-white/15"
      />
    </div>
  );
}
